import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Modal,
  Button,
  Alert,
  Badge,
  Box,
  Group,
  LoadingOverlay,
  Progress,
  ScrollArea,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import apiService from '../services/api';
import { useTranslation } from '../context/TranslationContext';
import { formatPrice } from '../utils/formatPrice';
import { getShippingDominantRate } from '../utils/shippingVat';
import { creditAmountFromItems, splitByCredit } from '../utils/orderBreakdown';
import PriceBreakdown from './PriceBreakdown';
import './OngoingOrderModal.css';

// Admin view of a running ("ongoing") monthly order.
//
// A running order accumulates batches over a month, so this modal is where the
// admin sees where the money has got to and closes it. Closing emails the
// customer once and makes the order eligible for the monthly invoice; a closed
// order is read-only from then on.
//
// Money back is a negative-quantity line entered in the order-taking form, not a
// separate store: it rides along in the order's own item list, so there is nothing
// to add or remove here. Those lines are deliberately NOT routed through the normal
// refund flow — nothing was shipped, so there is no stock to put back and no Stripe
// charge to reverse.

const round1 = (n) => Math.round(Number(n) * 10) / 10;

// When a line entered the running order. Stored as UTC by MySQL; rendered as a plain
// day so it lines up with the order date the admin typed.
function formatAddedAt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

export default function OngoingOrderModal({ orderId, onClose, onChanged }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmingComplete, setConfirmingComplete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await apiService.getOngoingOrder(orderId));
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const order = data?.order;
  const items = useMemo(() => data?.items || [], [data]);
  // Refund lines are negative quantities — money back, never goods.
  const { goods } = useMemo(() => splitByCredit(items), [items]);
  // Memoised so the breakdown useMemo below does not see a fresh object identity
  // on every render.
  const snapshot = useMemo(() => data?.snapshot || {}, [data]);
  const vatExclusive = !!data?.vatExclusive;
  // Aggregation state is a fact, not a status (see utils/runningOrder.js), so the
  // API states it explicitly. A closed order is read-only: it cannot be closed
  // again and no further batch can be added to it.
  const isOpen = data?.open !== false;

  // Retail shows GROSS, so NET ledger amounts are grossed up at the order's
  // dominant rate — same rule the invoice and order emails use. Refund lines are
  // left out: they are money back, not shipped goods, and must not drag the rate.
  const grossFactor = useMemo(() => {
    if (vatExclusive) return 1;
    const rate = getShippingDominantRate(goods.map(i => ({
      net: (Number(i.unit_price_final) || 0) * (Number(i.quantity) || 1),
      vat_rate: Number(i.vat_rate) || 0,
    })));
    return 1 + rate / 100;
  }, [goods, vatExclusive]);

  // The cart discount resolves on the merchandise net minus the earlier
  // discounts, so its base is reconstructed from the same snapshot the engines
  // wrote — a running order's frozen ledger supersedes the rule/volume pair.
  const ledgerNet = useMemo(() => (Array.isArray(snapshot.discount_rows)
    ? snapshot.discount_rows.reduce((sum, r) => sum + Number(r.amount_net || 0), 0)
    : Number(snapshot.rule_discount || 0) + Number(snapshot.volume_discount || 0)), [snapshot]);

  const breakdown = useMemo(() => ({
    lang: t.langCode,
    vatExclusive,
    couponDiscounts: [],
    discountRows: Array.isArray(snapshot.discount_rows)
      ? snapshot.discount_rows.map(r => ({ ...r, amount_net: Math.abs(Number(r.amount_net || 0)) * grossFactor }))
      : null,
    cartDiscountPercent: Number(snapshot.cart_discount_percent || 0),
    // Grossed like every other NET amount here so retail shows the same figure the
    // invoice will; business keeps the net basis.
    cartDiscountBase: Math.max(0, Number(snapshot.net_subtotal || 0) - ledgerNet) * grossFactor,
    creditAmount: creditAmountFromItems(items, { vatExclusive }),
    ruleDiscount: Number(snapshot.rule_discount || 0),
    shippingCost: Number(snapshot.net_shipping ?? order?.shipping_cost ?? 0) * grossFactor,
    // The client-side buildBreakdownRows does NOT gross shipping itself (the
    // backend one does), so `shippingCost` above is already the displayed amount.
    // `netShipping` is only passed for the business view, where it *is* the net
    // amount shown — passing it in retail mode would leak the NET figure.
    netShipping: vatExclusive ? Number(snapshot.net_shipping ?? order?.shipping_cost ?? 0) : 0,
    shippingFree: !!snapshot.shipping_free,
    isPickup: !!(order && order.pickup_location_id),
    volumeDiscount: Number(snapshot.volume_discount || 0),
    volumeDiscountPercentage: snapshot.volume_discount_percentage ?? null,
    appliedBalance: Number(snapshot.applied_balance || 0),
    roundOffDiff: Number(snapshot.round_off_diff || 0),
    netSubtotal: Number(snapshot.net_subtotal || 0),
    vatAmount: Number(order?.vat_amount || 0),
  }), [items, snapshot, order, vatExclusive, grossFactor, ledgerNet, t.langCode]);

  // A non-zero rounding residual means the rows do not add up to the payable
  // exactly; the asterisk points at the rounding-difference row above.
  const hasRounding = Math.round(Number(snapshot.round_off_diff || 0) * 100) !== 0;

  // Fulfillment progress is derived from what was actually shipped, never from
  // the order status — a running order has no meaningful single shipment state.
  const shippedPieces = useMemo(() => goods.reduce((s, i) => s + (Number(i.quantity) || 0), 0), [goods]);
  const shippedTotal = Number(data?.shippedTotal || 0);
  const shipProgress = shippedPieces > 0 ? Math.min(100, Math.round((shippedTotal / shippedPieces) * 100)) : 0;

  const handleComplete = async () => {
    setBusy(true);
    try {
      await apiService.completeOngoingOrder(orderId);
      notifications.show({
        title: t('ongoingCompleted', 'Running order completed'),
        message: t('ongoingCompletedMsg', 'The customer has been emailed and the order can now be invoiced.'),
        color: 'green',
      });
      if (onChanged) onChanged();
      onClose();
    } catch (err) {
      notifications.show({ title: t('error', 'Error'), message: err.message, color: 'red' });
      setConfirmingComplete(false);
    } finally {
      setBusy(false);
    }
  };

  const shortId = String(orderId).substring(0, 8).toUpperCase();

  return (
    <Modal
      opened
      onClose={onClose}
      size="xl"
      title={t('ongoingOrderTitle', 'Running monthly order')}
      centered
    >
      <LoadingOverlay visible={loading} zIndex={200} overlayProps={{ blur: 2 }} />
      {loadError && (
        <Alert icon={<IconAlertCircle size={16} aria-hidden="true" />} color="red" mb="md">
          {loadError}
        </Alert>
      )}

      {order && !isOpen && (
        <Alert color="green" icon={<IconCheck size={16} aria-hidden="true" />} mb="md">
          {t(
            'ongoingAlreadyClosed',
            'This running order has been closed. It is ready for the monthly invoice and can no longer be changed.'
          )}
        </Alert>
      )}

      {order && (
        <>
          <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
            <Group gap="xs">
              <Badge color={isOpen ? 'blue' : 'green'} variant="light">
                {isOpen ? t('ongoingBadge', 'Running') : t('ongoingBadgeClosed', 'Running (closed)')}
              </Badge>
              <Text size="sm" c="dimmed">
                {t('ongoingOrderId', 'Order')} {shortId}
              </Text>
            </Group>
            <Group gap="lg">
              <Text size="sm">
                <Text span c="dimmed">{t('ongoingItemsCount', 'Lines')}: </Text>
                {items.length}
              </Text>
              <Text size="sm">
                <Text span c="dimmed">{t('total', 'Total')}: </Text>
                <Text span fw={600}>{formatPrice(order.total_price)}</Text>
              </Text>
            </Group>
          </Group>

          {shippedPieces > 0 && (
            <Box mb="md">
              <Group justify="space-between" mb={4}>
                <Text size="sm" c="dimmed">{t('ongoingShipProgress', 'Shipped')}</Text>
                <Text size="sm" c="dimmed">{shipProgress}%</Text>
              </Group>
              <Progress value={shipProgress} aria-label={t('ongoingShipProgress', 'Shipped')} />
            </Box>
          )}

          {/* Items */}
          <Text fw={600} size="sm" mb="xs">{t('items', 'Items')}</Text>
          <ScrollArea.Autosize mah={220} mb="md">
            <Table withTableBorder striped verticalSpacing="xs" fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th scope="col">{t('addedAt', 'Added')}</Table.Th>
                  <Table.Th scope="col">{t('product', 'Product')}</Table.Th>
                  <Table.Th scope="col" ta="right">{t('quantity', 'Quantity')}</Table.Th>
                  <Table.Th scope="col" ta="right">{t('discount', 'Discount')}</Table.Th>
                  <Table.Th scope="col" ta="right">{t('unitPrice', 'Unit price')}</Table.Th>
                  <Table.Th scope="col" ta="right">{t('total', 'Total')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map(i => {
                  const net = Number(i.unit_price_final) || 0;
                  const qty = Number(i.quantity) || 0;
                  const isCredit = qty < 0;
                  // A refund line is shown at its own VAT rate, not the order's
                  // dominant rate: it is money back on that one line, not goods.
                  const shown = vatExclusive || isCredit
                    ? net
                    : Math.round(net * grossFactor * 100) / 100;
                  const isFree = net === 0 && !isCredit;
                  // The admin's per-line manual discount: the agreed base is shown
                  // struck through next to the discounted price, as on invoices.
                  const discountPercent = Math.min(100, Math.max(0, Number(i.discount_percent) || 0));
                  const base = Number(i.unit_price_before_discount);
                  const showBase = discountPercent > 0 && Number.isFinite(base) && base > 0;
                  const shownBase = vatExclusive
                    ? base
                    : Math.round(base * grossFactor * 100) / 100;
                  const addedAt = formatAddedAt(i.created_at);
                  return (
                    <Table.Tr key={i.id}>
                      <Table.Td>
                        <Text span size="xs" c="dimmed">{addedAt}</Text>
                      </Table.Td>
                      <Table.Td>
                        {i.name || i.product_sku}
                        {i.product_sku && <Text span size="xs" c="dimmed"> · {i.product_sku}</Text>}
                        {isCredit && (
                          <Badge size="xs" color="orange" variant="light" ml={6}>
                            {t('refund', 'Refund')}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">{qty}</Table.Td>
                      <Table.Td ta="right">
                        {discountPercent > 0 ? (
                          <Text span c="green" fw={600}>{round1(discountPercent)} %</Text>
                        ) : (
                          <Text span c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        {isFree ? t('free', 'Free') : (
                          <>
                            {showBase && (
                              <Text span size="xs" c="dimmed" td="line-through" mr={6}>
                                {formatPrice(shownBase)}
                              </Text>
                            )}
                            {formatPrice(shown)}
                          </>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text span c={isFree ? 'green' : isCredit ? '#c2410c' : undefined} fw={isCredit ? 600 : undefined}>
                          {isFree ? t('free', 'Free') : formatPrice(shown * qty)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>

          <PriceBreakdown
            rows={null}
            t={t}
            lang={t.langCode}
            containerClass="price-breakdown ongoing-order-breakdown"
            // The money of record: orders.total_price is the gross payable, already
            // rounded to 5 Rappen. The rows above are the NET/gross ledger it is
            // derived from, so the last line has to be stated explicitly.
            total={Number(order?.total_price || 0)}
            totalLabel={hasRounding
              ? t('totalPayable')
              : t('total', 'Total')}
            {...breakdown}
          />

          {/* Close */}
          {confirmingComplete ? (
            <Alert
              color="orange"
              icon={<IconAlertCircle size={16} aria-hidden="true" />}
              title={t('ongoingCompleteConfirmTitle', 'Complete this running order?')}
            >
              <Text size="sm" mb="sm">
                {t(
                  'ongoingCompleteConfirmMsg',
                  'This closes the running order, emails the customer, and makes it eligible for the monthly invoice. The customer is no longer emailed again, and no further batches can be added.'
                )}
              </Text>
              <Group gap="xs">
                <Button
                  color="orange"
                  onClick={handleComplete}
                  loading={busy}
                  leftSection={<IconCheck size={16} aria-hidden="true" />}
                >
                  {t('ongoingCompleteYes', 'Yes, complete it')}
                </Button>
                <Button variant="default" onClick={() => setConfirmingComplete(false)} disabled={busy}>
                  {t('cancel', 'Cancel')}
                </Button>
              </Group>
            </Alert>
          ) : (
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>{t('close', 'Close')}</Button>
              <Button
                color="orange"
                onClick={() => setConfirmingComplete(true)}
                disabled={items.length === 0 || !isOpen}
                leftSection={<IconCheck size={16} aria-hidden="true" />}
              >
                {t('ongoingCompleteBtn', 'Complete running order')}
              </Button>
            </Group>
          )}
          {items.length === 0 && (
            <Text size="xs" c="dimmed" ta="right" mt="xs">
              {t('ongoingCannotCompleteEmpty', 'Add at least one item before completing.')}
            </Text>
          )}
        </>
      )}
    </Modal>
  );
}